import { Injectable, signal } from '@angular/core';
import { SupplierPlanResponse } from '../models/supplier.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly TOKEN_KEY = 'supplier_token';
  private readonly PLAN_KEY = 'supplier_plan';
  private isLoggedIn = signal(false);
  private planStatus = signal<SupplierPlanResponse | null>(null);

  constructor() {
    // Check if token exists on service initialization
    this.checkAuthStatus();
  }

  /**
   * Save token to sessionStorage and update auth status
   * @param token JWT token from login response
   */
  saveToken(token: string): void {
    sessionStorage.setItem(this.TOKEN_KEY, token);
    this.isLoggedIn.set(true);
  }

  /**
   * Get token from sessionStorage
   * @returns JWT token or null if not found
   */
  getToken(): string | null {
    return sessionStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Remove token from sessionStorage and update auth status
   */
  logout(): void {
    sessionStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(this.PLAN_KEY);
    this.isLoggedIn.set(false);
    this.planStatus.set(null);
  }

  /**
   * Check if user is authenticated
   * @returns boolean indicating auth status
   */
  isAuthenticated(): boolean {
    return this.isLoggedIn();
  }

  /**
   * Get auth status as signal for reactive updates
   * @returns signal with auth status
   */
  getAuthStatus() {
    return this.isLoggedIn.asReadonly();
  }

  /**
   * Save plan status to session and update signal
   */
  setPlanStatus(plan: SupplierPlanResponse): void {
    sessionStorage.setItem(this.PLAN_KEY, JSON.stringify(plan));
    this.planStatus.set(plan);
  }

  /**
   * Get current plan status signal (readonly)
   */
  getPlanStatus() {
    return this.planStatus.asReadonly();
  }

  /**
   * Check auth status based on token existence
   */
  private checkAuthStatus(): void {
    const token = this.getToken();
    this.isLoggedIn.set(!!token);
    const planJson = sessionStorage.getItem(this.PLAN_KEY);
    if (planJson) {
      try {
        this.planStatus.set(JSON.parse(planJson));
      } catch {
        // ignore parse errors
      }
    }
  }
}
